/**
 * Hangar Units - Individual units within T-row and nested-T hangars
 */

/** Hangar unit status */
export type HangarUnitStatus = 'available' | 'leased' | 'maintenance'

/** Hangar unit status display labels */
export const HANGAR_UNIT_STATUS_LABELS: Record<HangarUnitStatus, string> = {
  available: 'Available',
  leased: 'Leased',
  maintenance: 'Maintenance',
}

/** All hangar unit statuses */
export const HANGAR_UNIT_STATUSES: HangarUnitStatus[] = [
  'available',
  'leased',
  'maintenance',
]

/** Database row type for units table (formerly hangar_units) */
export interface HangarUnitRow {
  id: string
  building_id: string
  organization_id: string
  unit_code: string
  sequence: number
  door_width: number | null
  depth: number | null
  tail_width: number | null
  offset_x: number
  offset_y: number
  is_leasable: boolean
  status: HangarUnitStatus
  current_lease_id: string | null
  // Pricing override fields
  pricing_override: boolean
  unit_monthly_rate: number | null
  unit_rate_per_sqft: number | null
  // Audit fields
  created_at: string
  created_by: string | null
  updated_at: string | null
  updated_by: string | null
  deleted_at: string | null
  deleted_by: string | null
}

/** Insert type for creating new units */
export interface HangarUnitInsert {
  building_id: string
  organization_id: string
  unit_code: string
  sequence: number
  door_width?: number | null
  depth?: number | null
  tail_width?: number | null
  offset_x?: number
  offset_y?: number
  is_leasable?: boolean
  status?: HangarUnitStatus
  pricing_override?: boolean
  unit_monthly_rate?: number | null
  unit_rate_per_sqft?: number | null
}

/** Update type for modifying units */
export interface HangarUnitUpdate {
  unit_code?: string
  sequence?: number
  door_width?: number | null
  depth?: number | null
  tail_width?: number | null
  offset_x?: number
  offset_y?: number
  is_leasable?: boolean
  status?: HangarUnitStatus
  pricing_override?: boolean
  unit_monthly_rate?: number | null
  unit_rate_per_sqft?: number | null
}

/** Form data interface (what the form submits) */
export interface HangarUnitFormData {
  unit_code: string
  sequence: number
  door_width?: number | null
  depth?: number | null
  tail_width?: number | null
  offset_x: number
  offset_y: number
  is_leasable: boolean
  status: HangarUnitStatus
  pricing_override?: boolean
  unit_monthly_rate?: number | null
  unit_rate_per_sqft?: number | null
}

/** Table row type (subset for list display) */
export interface HangarUnitTableRow {
  id: string
  unit_code: string
  sequence: number
  door_width: number | null
  depth: number | null
  status: HangarUnitStatus
  is_leasable: boolean
}

/** Row position for nested T-hangars */
export type UnitRow = 'top' | 'bottom'

/** T-row configuration wizard form data */
export interface TRowConfigFormData {
  unit_count: number
  nesting_style: 'standard' | 'offset'
  door_width: number
  depth: number
  tail_width: number
  taxilane_width: number
  /** Layout type - single row or nested (teeth) */
  layout: 'single' | 'nested'
  /** For nested layout: units per row (if different from unit_count) */
  units_per_row?: number
}

/** Generated unit from template (before saving) */
export interface GeneratedUnit {
  unit_code: string
  sequence: number
  door_width: number
  depth: number
  tail_width: number
  offset_x: number
  offset_y: number
  /** Which row this unit belongs to (for nested layout) */
  row?: UnitRow
}
